import React, { createContext, useContext, useEffect, useRef, ReactNode } from 'react';
import { AuthClient } from '../services/apiClient';
import { API_BASE_URL } from '../config/config';
import axiosInstance from '../services/axiosInstance';
import {
  getSecondsUntilExpiry,
  getToken,
  getRefreshToken,
  setTokens,
  isTokenExpired,
} from '../utils/tokenUtils';

interface AuthContextType {
  // Context can be extended with additional auth methods if needed
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Manages background token refresh
 * Refreshes token 2 minutes (120 seconds) before expiration
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRefreshingRef = useRef<boolean>(false);

  /**
   * Refresh the access token using the refresh token
   */
  const refreshAccessToken = async (): Promise<void> => {
    // Prevent concurrent refresh attempts
    if (isRefreshingRef.current) {
      return;
    }

    const token = getToken();
    const refreshToken = getRefreshToken();

    if (!token || !refreshToken) {
      return;
    }

    try {
      isRefreshingRef.current = true;

      const tokenClient = new AuthClient(API_BASE_URL, axiosInstance);
      const { data } = await tokenClient.refreshToken({ refreshToken });

      // Store tokens in appropriate storage (automatically handles impersonation)
      if (data?.accessToken && data?.refreshToken) {
        setTokens(data.accessToken, data.refreshToken);
      }
    } catch (error) {
      console.error('Background token refresh failed:', error);

      // Don't immediately redirect on background refresh failure
      // Let the axios interceptor and PrivateRoute handle auth failures
      // This prevents flashing when API is temporarily down
    } finally {
      isRefreshingRef.current = false;
    }
  };

  /**
   * Schedule the next token refresh
   * Refreshes 2 minutes (120 seconds) before token expiration
   */
  const scheduleTokenRefresh = (): void => {
    // Clear existing timeout
    if (refreshIntervalRef.current) {
      clearTimeout(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    const token = getToken();
    if (!token) {
      return;
    }

    const secondsUntilExpiry = getSecondsUntilExpiry();

    // Don't attempt immediate refresh - let axios interceptor handle expired tokens
    // This prevents API calls on mount when API might not be ready
    if (secondsUntilExpiry < 120) {
      return;
    }

    // Calculate when to refresh: 2 minutes (120 seconds) before expiration
    const refreshThresholdSeconds = 120;
    const secondsUntilRefresh = Math.max(0, secondsUntilExpiry - refreshThresholdSeconds);

    // Schedule refresh using setTimeout (converted to milliseconds)
    const timeoutId = setTimeout(() => {
      refreshAccessToken().then(() => {
        // After successful refresh, schedule the next refresh
        scheduleTokenRefresh();
      });
    }, secondsUntilRefresh * 1000);

    // Store timeout reference for cleanup
    refreshIntervalRef.current = timeoutId as unknown as NodeJS.Timeout;
  };

  /**
   * Initialize background refresh on mount
   * Only schedule if token exists AND is not expired
   */
  useEffect(() => {
    const token = getToken();

    // Only schedule background refresh for valid, non-expired tokens
    // Let PrivateRoute and axios interceptor handle expired tokens
    if (token && !isTokenExpired()) {
      scheduleTokenRefresh();
    }

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearTimeout(refreshIntervalRef.current);
      }
    };
  }, []);

  /**
   * Listen for token changes (e.g., after login)
   * This ensures refresh is scheduled when user logs in
   */
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent): void => {
      if (event.key === 'token' && event.newValue) {
        scheduleTokenRefresh();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{}}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use AuthContext
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
